import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getSkus,
  getSkuById,
  createSku,
  updateSku,
  deleteSku,
  checkSkuUsage,
  replaceSkuBom,
  replaceSkuSuppliers,
  searchSkusApi,
} from '../api';
import type {
  SkuFilters,
  CreateSkuInput,
  UpdateSkuInput,
} from '../types';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred';
}

export const wmsSkuKeys = {
  all: ['wms-skus'] as const,
  list: (filters?: SkuFilters) => [...wmsSkuKeys.all, 'list', filters] as const,
  detail: (id: string) => [...wmsSkuKeys.all, 'detail', id] as const,
  usage: (id: string) => [...wmsSkuKeys.all, 'usage', id] as const,
  search: (term?: string) => [...wmsSkuKeys.all, 'search', term] as const,
};

export function useSkus(filters: SkuFilters = {}) {
  return useQuery({
    queryKey: wmsSkuKeys.list(filters),
    queryFn: () => getSkus(filters),
  });
}

export function useSku(id: string | undefined) {
  return useQuery({
    queryKey: wmsSkuKeys.detail(id!),
    queryFn: () => getSkuById(id!).then((r) => r.sku),
    enabled: !!id,
  });
}

export function useSkuUsage(id: string | undefined) {
  return useQuery({
    queryKey: wmsSkuKeys.usage(id!),
    queryFn: () => checkSkuUsage(id!),
    enabled: !!id,
  });
}

export function useCatalogSkuSearch(search: string | undefined, limit = 20, enabled = true) {
  return useQuery({
    queryKey: wmsSkuKeys.search(search),
    queryFn: () => searchSkusApi(search, limit).then((r) => r.skus),
    enabled: enabled && (search === undefined || search === '' || search.length >= 1),
  });
}

export function useCreateSku() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateSkuInput) => createSku(data).then((r) => r.sku),
    onSuccess: (sku) => {
      queryClient.invalidateQueries({ queryKey: wmsSkuKeys.all });
      toast.success(`SKU "${sku.sku}" created successfully`);
    },
    onError: (error) => {
      toast.error('Failed to create SKU', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateSku() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSkuInput }) =>
      updateSku(id, data).then((r) => r.sku),
    onSuccess: (sku, { id }) => {
      queryClient.invalidateQueries({ queryKey: wmsSkuKeys.all });
      queryClient.invalidateQueries({ queryKey: wmsSkuKeys.detail(id) });
      toast.success(`SKU "${sku.sku}" updated successfully`);
    },
    onError: (error) => {
      toast.error('Failed to update SKU', { description: getErrorMessage(error) });
    },
  });
}

export function useDeleteSku() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSku(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wmsSkuKeys.all });
      toast.success('SKU deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete SKU', { description: getErrorMessage(error) });
    },
  });
}

export function useReplaceSkuBom() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, items }: { id: string; items: Array<{ component_sku_id: string; qty: number }> }) =>
      replaceSkuBom(id, items),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: wmsSkuKeys.detail(id) });
      toast.success('BOM updated');
    },
    onError: (error) => {
      toast.error('Failed to update BOM', { description: getErrorMessage(error) });
    },
  });
}

export function useReplaceSkuSuppliers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, suppliers }: { id: string; suppliers: Array<{
      organisation_id: string;
      organisation_name?: string;
      supplier_sku?: string;
      link?: string;
      price?: number;
      lead_time_days?: number;
      moq?: number;
    }> }) => replaceSkuSuppliers(id, suppliers),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: wmsSkuKeys.detail(id) });
      toast.success('Suppliers updated');
    },
    onError: (error) => {
      toast.error('Failed to update suppliers', { description: getErrorMessage(error) });
    },
  });
}
